import { FormFieldWidget, ModelFieldType, SPI, ViewType, Widget, http } from '@kunlun/dependencies';
import { stringifyToGraphql } from '../MindMap/utils/serialize';
import EvaluationResultCard from './EvaluationResultCard.vue';

/**
 * 客户实时评估结果卡片 Widget
 *
 * <p>绑定字段：evaluationResultJson（String，不落库），同时适配平台端
 * DeliAriesPlatformCustomerDistributor 与品牌分销端 DeliAriesSellerDistributor。
 * <ul>
 *   <li>详情页（Detail）：只读后端从评估结果表回填的 JSON，不发起试算</li>
 *   <li>创建/编辑页（Form）：组件监听建档指标字段变更，防抖后调 {@link callEvaluatePreview}
 *       触发后端「评估试算」Action（不落库），实时刷新卡片</li>
 * </ul>
 * 设计器无需切换组件：Token 按 name 匹配默认 String 控件直接覆盖渲染。</p>
 */
@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: [ViewType.Form, ViewType.Detail],
    ttype: ModelFieldType.String,
    name: 'evaluationResultJson',
    model: [
      'deli.aries.seller.DeliAriesSellerDistributor',
      'deli.aries.platform.DeliAriesPlatformCustomerDistributor'
    ]
  })
)
export class FormEvaluationResultCardFieldWidget extends FormFieldWidget {
  public initialize(props) {
    super.initialize(props);
    this.setComponent(EvaluationResultCard);
    return this;
  }

  /** 是否表单视图（创建/编辑）；详情页只读不试算 */
  @Widget.Reactive()
  public get isFormView(): boolean {
    return this.getUrlParameters().viewType !== ViewType.Detail;
  }

  /**
   * 调后端「评估试算」Action：以当前表单的建档字段试算评估结果（不落库）。
   *
   * <p>省份列表仅取个数参与评估，故传空对象数组，避免表单运行时附带的
   * 额外字段导致 GraphQL 输入校验失败。</p>
   *
   * @param formData 当前表单全量数据
   * @returns 卡片 JSON；无启用规则/调用失败返回 null
   */
  @Widget.Method()
  public async callEvaluatePreview(formData: Record<string, any>): Promise<string | null> {
    const sizeOf = (list: unknown): object[] => (Array.isArray(list) ? list.map(() => ({})) : []);
    // 表单输入框的数值字段可能为字符串，GraphQL 要求 Int/Float 字面量，统一转数字
    const num = (v: unknown): number | null => {
      if (v === null || v === undefined || v === '') {
        return null;
      }
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    };
    const data = {
      businessProperty: formData?.businessProperty ?? null,
      cooperationSystemCount: num(formData?.cooperationSystemCount),
      deliBrandSlotCount: num(formData?.deliBrandSlotCount),
      existingBusinessScale: num(formData?.existingBusinessScale),
      monthlyAverageOrders: num(formData?.monthlyAverageOrders),
      systemStoreCount: num(formData?.systemStoreCount),
      flashCentralWarehouseProvinces: sizeOf(formData?.flashCentralWarehouseProvinces),
      supplyChainCentralWarehouseProvinces: sizeOf(formData?.supplyChainCentralWarehouseProvinces),
      // 共用指标：人数
      teamSize: num(formData?.teamSize),
      operationStaffCount: num(formData?.operationStaffCount),
      // 闪电仓指标：GMV
      systemAnnualGMV: num(formData?.systemAnnualGMV),
      learningOfficeGMV: num(formData?.learningOfficeGMV),
      deliBrandGMV: num(formData?.deliBrandGMV),
      // 供应链指标：多选选项个数 / 年销
      cooperationSystemName: sizeOf(formData?.cooperationSystemName),
      mainBrand: sizeOf(formData?.mainBrand),
      competitorAgentSituation: sizeOf(formData?.competitorAgentSituation),
      learningOfficeAnnualSales: num(formData?.learningOfficeAnnualSales),
      customerMainBusiness: sizeOf(formData?.customerMainBusiness)
    };
    // mutation 命名空间随字段所属模型推导：
    // 平台端 deliAriesPlatformCustomerDistributorMutation / 品牌分销端 deliAriesSellerDistributorMutation
    const simpleName: string = String((this as any).model?.model || '').split('.').pop() || '';
    const namespace = simpleName
      ? simpleName.charAt(0).toLowerCase() + simpleName.slice(1) + 'Mutation'
      : 'deliAriesPlatformCustomerDistributorMutation';
    const body = `mutation {
      ${namespace} {
        evaluatePreview(data: ${stringifyToGraphql(data, ['businessProperty'])}) {
          evaluationResultJson
        }
      }
    }`;
    try {
      const rst: any = await http.mutate((this as any).model?.moduleName, body);
      return rst?.data?.[namespace]?.evaluatePreview?.evaluationResultJson ?? null;
    } catch (e) {
      return null;
    }
  }
}
